import { DOCUMENT, NgIf, NgStyle } from '@angular/common';
import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FlexModule } from '@angular/flex-layout';
import { ExtendedModule } from '@angular/flex-layout/extended';
import { ReactiveFormsModule, UntypedFormGroup } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { AgGridAngular, AgGridModule } from 'ag-grid-angular';
import { ColDef, GridOptions, SelectionChangedEvent } from 'ag-grid-community';
import { DateFormatPipe } from 'ngx-moment';
import { Docket } from '../../models/index';
import { CustomerNameCellComponent } from '../../shared/ag-grid-cells/customer-name-cell.component';
import { DocketNumberAccountReferenceCellComponent } from '../../shared/ag-grid-cells/docket-number-account-reference-cell.component';
import { PreviewDocketCellComponent } from '../../shared/ag-grid-cells/preview-docket-cell.component';
import { dateComparator } from '../../shared/helpers/DateComparator';
import { DocketDataSource } from '../../shared/helpers/DocketDataSource';
import { DocketService } from '../../shared/index';
import { CustomerNamePipe } from '../../shared/pipes/customer-name.pipe';
import { ConfigService } from '../../shared/services/config.service';
import { ReportCriteriaItemModel } from '../shared/models/report-criteria-item.model';
import { ReportCriteriaComponent } from '../shared/report-criteria/report-criteria.component';
import { UpdateStatusDialogComponent } from './update-status-dialog/update-status-dialog.component';

@Component({
  selector: 'app-report',
  templateUrl: './docket-status-report.component.html',
  styleUrls: ['./docket-status-report.component.css'],
  providers: [DocketService, DateFormatPipe, CustomerNamePipe],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    FlexModule,
    ReportCriteriaComponent,
    NgIf,
    MatButton,
    AgGridModule,
    NgStyle,
    ExtendedModule
  ]
})
export class DocketStatusReportComponent implements OnInit {
  @ViewChild('formContainer', { static: true }) formContainer;
  @ViewChild('agGrid') grid!: AgGridAngular;

  statuses = [
    { value: 'Active', id: 'Active' },
    { value: 'Pending Payment', id: 'Pending Payment' },
    { value: 'Paid', id: 'Paid' },
    { value: 'To be transferred', id: 'To be transferred' },
    { value: 'Transferred', id: 'Transferred' },
    { value: 'All', id: '*' }
  ];
  columns: ColDef[] = [];

  availableCriteria: ReportCriteriaItemModel[] = [
    {
      name: 'Status',
      type: 'dropDown',
      reference: 'status',
      used: true,
      defaultValue: '*',
      options: this.statuses
    },
    {
      name: 'Created Date',
      type: 'fromToDate',
      reference: 'created',
      used: false
    },
    {
      name: 'Invoice Date',
      type: 'fromToDate',
      reference: 'closed',
      used: false
    }
  ];

  showBulkUpdateButton = false;

  gridOptions: GridOptions = {
    rowModelType: 'infinite',
    paginationPageSize: 500,
    cacheBlockSize: 500,
    headerHeight: 45,
    cacheOverflowSize: 2,
    maxConcurrentDatasourceRequests: 2,
    infiniteInitialRowCount: 1,
    rowSelection: 'multiple',
    rowMultiSelectWithClick: true,
    onSelectionChanged: this.onSelectionChanged.bind(this)
  };

  selectedStatus: string;

  selectedRows: Docket[] = [];

  reportForm = new UntypedFormGroup({});

  reportFetched = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private docketService: DocketService,
    private datePipe: DateFormatPipe,
    private dialog: MatDialog
  ) {}

  ngOnInit() {
    this.columns = [
      {
        headerName: '',
        checkboxSelection: true
      },
      {
        headerName: 'Customer',
        cellRenderer: CustomerNameCellComponent,
        field: 'full_name',
        colId: 'full_name'
      },
      {
        headerName: 'Contract',
        field: 'id',
        colId: 'id',
        cellRendererSelector: (params) => {
          // if (params.node.rowPinned) {
          //   console.log('row pinned!', { params });
          //   return {
          //     component: undefined
          //   };
          // }
          return {
            component: DocketNumberAccountReferenceCellComponent
          };
        },
        valueGetter: (params) => {
          return params.data;
        }
      },
      {
        headerName: 'Created Date',
        field: 'created',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['created'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Invoice Date',
        field: 'closed',
        valueGetter: (params) => {
          if (params.data) {
            return this.datePipe.transform(
              params.data['closed'],
              ConfigService.dates.shortDateTimeFormat
            );
          }
        },
        comparator: dateComparator
      },
      {
        headerName: 'Hires',
        field: 'hires',
        valueFormatter: (params) => {
          return params.value
            ? Number(params.value).toFixed(2)
            : (0).toFixed(2);
        }
      },
      {
        headerName: 'Consumables',
        field: 'consumables',
        valueFormatter: (params) => {
          return params.value
            ? Number(params.value).toFixed(2)
            : (0).toFixed(2);
        }
      },
      {
        headerName: 'Deliveries',
        field: 'deliveries',
        valueFormatter: (params) => {
          return params.value
            ? Number(params.value).toFixed(2)
            : (0).toFixed(2);
        }
      },
      {
        headerName: 'Preview',
        cellRenderer: PreviewDocketCellComponent
      }
    ];
  }

  getDockets(isExport = false) {
    this.reportFetched = true;
    this.selectedStatus = this.reportForm.value['status'].value;
    const used = {};
    this.availableCriteria.forEach((available) => {
      if (available.used) {
        used[available.reference] = {
          type: available.type,
          value: available.formControl.value
        };
      }
    });
    used['status'] = {
      type: 'dropDown',
      value: {
        operator: '=',
        value: this.selectedStatus
      }
    };
    if (isExport) {
      this.exportReporToXls(used);
      return;
    }
    const dataSource = new DocketDataSource(this.docketService, used);
    this.grid.api.setGridOption('datasource', dataSource);
    this.showBulkUpdateButton = this.selectedStatus === 'To be transferred';
    this.docketService
      .generateReport({ summary: true, excludeDeleted: 1 }, used)
      .subscribe((results) => {
        this.grid.api.setGridOption('pinnedBottomRowData', [{ ...results }]);
      });
    this.reportForm.markAsPristine();
  }

  exportReporToXls(used) {
    this.docketService
      .generateReportXls(
        {
          excludeDeleted: 1,
          export: true
        },
        used
      )
      .subscribe((data) => {
        const blob = new Blob([data] as any, {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        });
        const url = this.document.defaultView.URL.createObjectURL(blob);
        const a = this.document.createElement('a');
        this.document.body.appendChild(a);
        a.setAttribute('style', 'display: none');
        a.href = url;
        a.download = 'Docket-Status.xlsx';
        a.click();
        this.document.defaultView.URL.revokeObjectURL(url);
        a.remove(); // remove the element
      });
  }

  onSelectionChanged(event: SelectionChangedEvent) {
    this.selectedRows = event.api.getSelectedRows();
  }

  getItemsString(params) {
    return params.data.items.length;
  }

  showStatusUpdateDialog() {
    const dialogData = {
      selected: this.selectedRows,
      status: 'Transferred',
      currentStatus: 'To be transferred'
    };

    const dialogRef = this.dialog.open(UpdateStatusDialogComponent, {
      data: dialogData
    });

    dialogRef.afterClosed().subscribe((updateClicked) => {
      if (updateClicked) {
        this.getDockets();
      }
    });
  }
}
